/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { MediaField } from "components/AboutUsContentForm/MediaField";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { AVAILABLE_SERVICES } from "constants/general";
import { getPickupDelivery, updatePickupDelivery } from "actions";
import "./PickupDelivery.scss";
import { BrandButton } from "components/brand";
import { FILE_TYPE } from "constants/fileTypes";

const imageBase = process.env.REACT_APP_API_IMAGE_URL;

export const PickupDelivery = () => {
  const dispatch = useDispatch();
  const { pickupDeliveryItems, loading } = useSelector(
    (state) => state.pickupDelivery,
  );

  const [pickupFile, setPickupFile] = useState(null);
  const [pickupPreviewUrl, setPickupPreviewUrl] = useState(null);
  const [pickupIsVideo, setPickupIsVideo] = useState(false);
  const [pickupLink, setPickupLink] = useState("");
  const [pickupSaving, setPickupSaving] = useState(false);

  const [deliveryFile, setDeliveryFile] = useState(null);
  const [deliveryPreviewUrl, setDeliveryPreviewUrl] = useState(null);
  const [deliveryIsVideo, setDeliveryIsVideo] = useState(false);
  const [deliveryLink, setDeliveryLink] = useState("");
  const [deliverySaving, setDeliverySaving] = useState(false);

  const handlePickupChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPickupFile(file);
    setPickupIsVideo(file.type.startsWith("video"));
    setPickupPreviewUrl(URL.createObjectURL(file));
  };

  const handlePickupRemove = () => {
    const pickup = pickupDeliveryItems?.find(
      (i) => i.service === AVAILABLE_SERVICES.PICKUP,
    );
    setPickupFile(null);
    setPickupPreviewUrl(pickup?.image ? `${imageBase}${pickup.image.path}` : null);
    setPickupIsVideo(pickup?.image?.type === FILE_TYPE.VIDEO || false);
  };

  const handleDeliveryChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDeliveryFile(file);
    setDeliveryIsVideo(file.type.startsWith("video"));
    setDeliveryPreviewUrl(URL.createObjectURL(file));
  };

  const handleDeliveryRemove = () => {
    const delivery = pickupDeliveryItems?.find(
      (i) => i.service === AVAILABLE_SERVICES.DELIVERY,
    );
    setDeliveryFile(null);
    setDeliveryPreviewUrl(delivery?.image ? `${imageBase}${delivery.image.path}` : null);
    setDeliveryIsVideo(delivery?.image?.type === FILE_TYPE.VIDEO || false);
  };

  const handlePickupSave = async (e) => {
    e.preventDefault();
    setPickupSaving(true);
    try {
      await dispatch(
        updatePickupDelivery({
          service: AVAILABLE_SERVICES.PICKUP,
          imageFile: pickupFile || undefined,
          link: pickupLink,
        }),
      ).unwrap();
      setPickupFile(null);
    } finally {
      setPickupSaving(false);
    }
  };

  const handleDeliverySave = async (e) => {
    e.preventDefault();
    setDeliverySaving(true);
    try {
      await dispatch(
        updatePickupDelivery({
          service: AVAILABLE_SERVICES.DELIVERY,
          imageFile: deliveryFile || undefined,
          link: deliveryLink,
        }),
      ).unwrap();
      setDeliveryFile(null);
    } finally {
      setDeliverySaving(false);
    }
  };

  useEffect(() => {
    dispatch(getPickupDelivery());
  }, []);

  useEffect(() => {
    if (pickupDeliveryItems) {
      const pickup = pickupDeliveryItems.find(
        (i) => i.service === AVAILABLE_SERVICES.PICKUP,
      );
      const delivery = pickupDeliveryItems.find(
        (i) => i.service === AVAILABLE_SERVICES.DELIVERY,
      );

      if (pickup?.image && !pickupFile) {
        setPickupPreviewUrl(`${imageBase}${pickup.image.path}`);
        setPickupIsVideo(pickup.image.type === FILE_TYPE.VIDEO);
      }
      if (pickup?.link !== undefined) {
        setPickupLink(pickup.link ?? "");
      }
      if (delivery?.image && !deliveryFile) {
        setDeliveryPreviewUrl(`${imageBase}${delivery.image.path}`);
        setDeliveryIsVideo(delivery.image.type === FILE_TYPE.VIDEO);
      }
      if (delivery?.link !== undefined) {
        setDeliveryLink(delivery.link ?? "");
      }
    }
  }, [pickupDeliveryItems]);

  if (loading && !pickupPreviewUrl && !deliveryPreviewUrl) {
    return (
      <Box className="pickup-delivery-page">
        <Typography variant="h4" component="h1" fontWeight="bold" mb={3}>
          {ADMIN_TEXTS.pickupDelivery.pageTitle}
        </Typography>
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box className="pickup-delivery-page">
      <Typography variant="h4" component="h1" fontWeight="bold" mb={3}>
        {ADMIN_TEXTS.pickupDelivery.pageTitle}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <form onSubmit={handlePickupSave} className="pickup-delivery-form">
                <MediaField
                  label={ADMIN_TEXTS.pickupDelivery.pickupImage}
                  file={pickupFile}
                  previewUrl={pickupPreviewUrl}
                  isVideo={pickupIsVideo}
                  onFileChange={handlePickupChange}
                  onRemove={handlePickupRemove}
                />
                <TextField
                  fullWidth
                  label={ADMIN_TEXTS.pickupDelivery.pickupLinkLabel}
                  value={pickupLink}
                  onChange={(e) => setPickupLink(e.target.value)}
                  helperText={ADMIN_TEXTS.pickupDelivery.linkHelperText}
                  sx={{ mt: 2 }}
                />
                <Box className="form-footer">
                  <BrandButton
                    type="submit"
                    variant="primary"
                    disabled={pickupSaving}
                    startIcon={
                      pickupSaving ? <CircularProgress size={18} /> : <Save />
                    }
                  >
                    {pickupSaving
                      ? ADMIN_TEXTS.actions.saving
                      : ADMIN_TEXTS.common.save}
                  </BrandButton>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <form onSubmit={handleDeliverySave} className="pickup-delivery-form">
                <MediaField
                  label={ADMIN_TEXTS.pickupDelivery.deliveryImage}
                  file={deliveryFile}
                  previewUrl={deliveryPreviewUrl}
                  isVideo={deliveryIsVideo}
                  onFileChange={handleDeliveryChange}
                  onRemove={handleDeliveryRemove}
                />
                <TextField
                  fullWidth
                  label={ADMIN_TEXTS.pickupDelivery.deliveryLinkLabel}
                  value={deliveryLink}
                  onChange={(e) => setDeliveryLink(e.target.value)}
                  helperText={ADMIN_TEXTS.pickupDelivery.linkHelperText}
                  sx={{ mt: 2 }}
                />
                <Box className="form-footer">
                  <BrandButton
                    type="submit"
                    variant="primary"
                    disabled={deliverySaving}
                    startIcon={
                      deliverySaving ? <CircularProgress size={18} /> : <Save />
                    }
                  >
                    {deliverySaving
                      ? ADMIN_TEXTS.actions.saving
                      : ADMIN_TEXTS.common.save}
                  </BrandButton>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
